import { Component, h, Prop, Element, State, Method } from '@stencil/core';
import { matchMediaMobile } from '../utils';

// selectors
const FADE_IN = 'adl-search--fadein';
const FADE_OUT = 'adl-search--fadeout';
const CTA_FADE_OUT = 'adl-search__cta--fadeout';
const WRAPPER_ACTIVE = 'adl-global-nav__search-wrapper--active';
const FORM_INPUT_INACTIVE = 'adl-search__input--inactive';
const HIDE_NAV_MENU = 'adl-search__nav-menu--hide';
const SEARCH_BAR_OPEN = 'adl-search__search-icon--open';
const FORM_VALID = 'adl-search__form--valid';
const CLEAR_SEARCH_ICON_ACTIVE = 'adl-search__clear-search-icon--active';
const CONTAINER = 'adl-search__container';
const CONTAINER_ACTIVE = 'adl-search__container--active';

// element map keys
const FORM = 'form';
const SEARCH_ICON = 'searchIcon';
const INPUT = 'input';
const CLEAR_SEARCH_ICON = 'clearSearchIcon';
const MENU = 'menu';
const SIGNIN = 'signIn';
const INPUT_CONTAINER = 'searchContainer';
const MENU_WRAPPER = 'searchWrapper';
const BRAND_CTA = 'brandCta';

/**
 * COMPNAME is for creating a search bar.
 *
 * <em>For More Information:</em>
 * AEM Component- ui.apps/src/main/content/jcr_root/apps/brand/aafp/components/nav/aafp-search
 * Sling Model- core/src/main/java/org/aafp/aem/core/models/nav/AafpSearchModel.java
 */
@Component({
  tag: 'aafp-search',
  shadow: false,
})
export class AafpSearch {
  /**
   * For creating the search bar's id
   */
  @Prop() inputId: string;
  @Prop({ mutable: true }) isHidden: boolean = false;
  nodeMap: Map<string, any> = new Map();
  @Element() element: HTMLElement;
  @State() searchBarActive: boolean = false;
  @State() addedListener: boolean = false;

  toggleClass({ classList }: Element, selectors: string[]): void {
    selectors.forEach((selector: string) => {
      return Array.from(classList).includes(selector) ? classList.remove(selector) : classList.add(selector);
    });
  }

  @Method()
  async toggleClassAsync(callback?: any) {
    const { classList, className } = callback();
    return new Promise((resolve, reject) => {
      try {
        const fn = () => {
          return className.map((selector: string) => {
            return Array.from(classList).includes(selector) ? classList.remove(selector) : classList.add(selector);
          });
        };
        resolve(fn());
      } catch (e) {
        reject(e);
      }
    });
  }

  getQuerySelectors() {
    if (this.addedListener) return;
    const querySelectors: { [key: string]: any } = {
      form: this.element.querySelector('form') as HTMLFormElement,
      searchIcon: this.element.querySelector('.adl-search__search-icon') as Element,
      clearSearchIcon: this.element.querySelector('.adl-search__clear-search-icon') as Element,
      menu: document.querySelector('.adl-global-nav__list') as Element,
      searchContainer: document.querySelector(`.${CONTAINER}`) as Element,
      searchWrapper: document.querySelector('.adl-global-nav__search-wrapper') as Element,
      input: document.querySelector(`#${this.inputId}`) as HTMLInputElement,
      brandCta: document.querySelector('.adl-global-nav__brand-cta--desktop'),
      signIn: document.querySelector('.adl-global-nav__signin-content'),
    };
    Object.keys(querySelectors).forEach((selectorKey: string) =>
      this.nodeMap.set(selectorKey, querySelectors[selectorKey]),
    );
  }

  addSearchFormInputEventHandler(): void {
    const icon = this.nodeMap.get(SEARCH_ICON) as Element;
    const input = this.nodeMap.get(INPUT) as HTMLInputElement;
    const inputHandler = (): void => {
      const hasInputValue = Array.from(input.value).length > 0;
      const iconStateIsValid = Array.from(icon.classList).includes(FORM_VALID);
      if (hasInputValue && !iconStateIsValid) this.toggleClass(icon, [FORM_VALID]);
      if (!hasInputValue && iconStateIsValid) this.toggleClass(icon, [FORM_VALID]);
    };
    input.addEventListener('input', inputHandler);
  }

  resetSearchInputIconOnClose(): void {
    const icon = this.nodeMap.get(SEARCH_ICON) as Element;
    this.nodeMap.get(INPUT).value = '';
    if (Array.from(icon.classList).includes(FORM_VALID)) {
      return this.toggleClass(icon, [FORM_VALID]);
    }
  }

  addClearSearchButtonClickEventHandler(): void {
    const onClickClearSearchIcon = (event: Event) => {
      event.preventDefault();
      this.nodeMap.get(INPUT).value = '';
      return matchMediaMobile() ? this.toggleClass(this.nodeMap.get(SEARCH_ICON), [FORM_VALID]) : this.element.click();
    };
    this.nodeMap.get(CLEAR_SEARCH_ICON).addEventListener('click', onClickClearSearchIcon);
  }

  addCloseSearchBarClickEventListener() {
    if (this.addedListener) return;
    const closeEventListener = ({ target }: any) => {
      if (!this.searchBarActive) return;
      const isSearchElement = target && target.hasAttribute('aafp-search');
      if (!isSearchElement) {
        this.searchBarActive = !this.searchBarActive;
        this.resetSearchInputIconOnClose();
        this.closeSearchBar();
      }
    };
    document.body.addEventListener('click', closeEventListener);
    this.addedListener = true;
  }

  closeSearchBar(): void {
    this.toggleClassAsync(() => {
      return {
        classList: this.nodeMap.get(FORM).classList,
        className: [FADE_IN, FADE_OUT],
      };
    }).then(() => {
      setTimeout(() => {
        this.toggleClass(this.nodeMap.get(MENU_WRAPPER), [WRAPPER_ACTIVE]);
        this.toggleClassAsync(() => {
          return {
            classList: this.nodeMap.get(MENU).classList,
            className: [FADE_OUT, HIDE_NAV_MENU, FADE_IN],
          };
        }).then(() => {
          this.toggleClass(this.nodeMap.get(INPUT_CONTAINER), [CONTAINER_ACTIVE]);
          this.toggleClass(this.nodeMap.get(CLEAR_SEARCH_ICON), [CLEAR_SEARCH_ICON_ACTIVE]);
          this.toggleClass(this.nodeMap.get(INPUT), [FORM_INPUT_INACTIVE]);
          this.toggleClass(this.nodeMap.get(FORM), [FADE_OUT]);
          this.toggleClass(this.nodeMap.get(SEARCH_ICON), [FADE_IN, SEARCH_BAR_OPEN]);
          if (this.nodeMap.get(BRAND_CTA)) this.toggleClass(this.nodeMap.get(BRAND_CTA), [CTA_FADE_OUT]);
          if (this.nodeMap.get(SIGNIN)) this.toggleClass(this.nodeMap.get(SIGNIN), [CTA_FADE_OUT]);
        });
      }, 200);
    });
  }

  showSearchInput(
    searchIcon: Element,
    menu: Element,
    form: Element,
    input: HTMLInputElement,
    menuSelectors: string[],
    brandCta: HTMLElement | undefined,
    signIn: HTMLElement | undefined,
  ): void {
    this.toggleClass(searchIcon, [FADE_OUT]);
    if (brandCta) this.toggleClass(brandCta, [CTA_FADE_OUT]);
    if (signIn) this.toggleClass(signIn, [CTA_FADE_OUT]);
    this.toggleClassAsync(() => {
      return {
        className: menuSelectors,
        classList: menu.classList,
      };
    }).then(() => {
      setTimeout(() => {
        this.toggleClass(this.nodeMap.get(MENU_WRAPPER), [WRAPPER_ACTIVE]);
        this.toggleClassAsync(() => {
          return {
            classList: form.classList,
            className: [FADE_IN],
          };
        }).then(() => {
          this.toggleClass(input, [FORM_INPUT_INACTIVE]);
          this.toggleClass(searchIcon, [FADE_OUT, FADE_IN, SEARCH_BAR_OPEN]);
          this.toggleClassAsync(() => {
            return {
              classList: menu.classList,
              className: [HIDE_NAV_MENU],
            };
          }).then(() => {
            this.toggleClass(this.nodeMap.get(INPUT_CONTAINER), [CONTAINER_ACTIVE]);
            this.toggleClass(this.nodeMap.get(CLEAR_SEARCH_ICON), [CLEAR_SEARCH_ICON_ACTIVE]);
            input.focus();
          });
        });
      }, 200);
    });
  }

  addSearchIconClickEventListener(): void {
    const searchIcon = this.nodeMap.get(SEARCH_ICON);
    const menu = this.nodeMap.get(MENU);
    const form = this.nodeMap.get(FORM);
    const input = this.nodeMap.get(INPUT);
    const brandCta = this.nodeMap.get(BRAND_CTA);
    const signIn = this.nodeMap.get(SIGNIN);
    searchIcon.addEventListener('click', (event: Event) => {
      if (this.searchBarActive || matchMediaMobile()) {
        return input.value.length > 0 ? form.submit() : event.preventDefault();
      }
      this.searchBarActive = !this.searchBarActive;
      this.addCloseSearchBarClickEventListener();
      const selectors = menu && Array.from(menu.classList).includes(FADE_IN) ? [FADE_IN, FADE_OUT] : [FADE_OUT];
      this.showSearchInput(searchIcon, menu, form, input, selectors, brandCta, signIn);
    });
  }

  addKeyboardEventListener(): void {
    const onKeyboardEvent = (event: any): void => {
      const { target, keyCode } = event;
      if (!target || keyCode !== 13) return;
      const submit = Array.from(target.classList).includes(FORM_VALID);
      const close = Array.from(target.classList).includes(CLEAR_SEARCH_ICON_ACTIVE);
      const open =
        Array.from(target.classList).includes('adl-search__search-icon') &&
        !Array.from(target.classList).includes(FORM_VALID);
      if (submit) return this.nodeMap.get(FORM).submit();
      if (close) return this.nodeMap.get(CLEAR_SEARCH_ICON).click();
      if (open) return this.nodeMap.get(SEARCH_ICON).click();
    };
    this.element.addEventListener('keydown', onKeyboardEvent);
  }

  render() {
    if (!this.isHidden) {
      this.getQuerySelectors();
      this.addSearchFormInputEventHandler();
      this.addClearSearchButtonClickEventHandler();
      this.addSearchIconClickEventListener();
      this.addKeyboardEventListener();
    }

    return (
      <div class="adl-search">
        <slot name="search-form"></slot>
      </div>
    );
  }
}
